import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail, MessageSquare, FileText, AlertCircle, CheckCircle, ArrowLeft, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PartnerNotificationSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // E-Mail Benachrichtigungen
    email_new_case_assigned: true,  // NEW
    email_new_case_status: true,
    email_case_completed: true,
    email_new_message: true,
    email_payment_received: true,
    email_monthly_report: true,
    email_marketing: false,
    // In-App Benachrichtigungen
    push_new_case_assigned: true,  // NEW
    push_new_case_status: true,
    push_case_completed: true,
    push_new_message: true,
    push_payment_received: true
  });

  const token = localStorage.getItem('partner_token');
  const partnerId = localStorage.getItem('partner_id');

  useEffect(() => {
    if (!token || !partnerId) {
      navigate('/portal/login');
      return;
    }
    fetchSettings();
  }, [token, partnerId, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partners/${partnerId}/notification-settings`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partners/${partnerId}/notification-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ settings })
        }
      );

      if (response.ok) {
        toast.success('Einstellungen gespeichert');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <div className="flex-1">
        <p className="font-medium text-gray-900">{label}</p>
        {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-brand-green' : 'bg-gray-300'
        }`}
        data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/portal/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <h1 className="text-xl font-bold text-gray-900">Benachrichtigungen</h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-brand-green hover:bg-brand-green-dark text-white"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>Speichern...</>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Speichern
              </>
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* E-Mail Benachrichtigungen */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-xl">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">E-Mail Benachrichtigungen</h2>
              <p className="text-sm text-gray-500">Wählen Sie, welche E-Mails Sie erhalten möchten</p>
            </div>
          </div>

          <div className="space-y-1">
            <ToggleSwitch
              checked={settings.email_new_case_assigned}
              onChange={(val) => setSettings({ ...settings, email_new_case_assigned: val })}
              label="Neue Fallzuweisung"
              description="E-Mail wenn Ihnen ein neuer Fall zugewiesen wird"
            />
            <ToggleSwitch
              checked={settings.email_new_case_status}
              onChange={(val) => setSettings({ ...settings, email_new_case_status: val })}
              label="Status-Updates"
              description="Benachrichtigung bei Änderung des Fallstatus"
            />
            <ToggleSwitch
              checked={settings.email_case_completed}
              onChange={(val) => setSettings({ ...settings, email_case_completed: val })}
              label="Gutachten fertig"
              description="E-Mail wenn ein Gutachten abgeschlossen wurde"
            />
            <ToggleSwitch
              checked={settings.email_new_message}
              onChange={(val) => setSettings({ ...settings, email_new_message: val })}
              label="Neue Nachrichten"
              description="Benachrichtigung bei neuen Chat-Nachrichten"
            />
            <ToggleSwitch
              checked={settings.email_payment_received}
              onChange={(val) => setSettings({ ...settings, email_payment_received: val })}
              label="Zahlungseingänge"
              description="Bestätigung bei eingegangenen Zahlungen"
            />
            <ToggleSwitch
              checked={settings.email_monthly_report}
              onChange={(val) => setSettings({ ...settings, email_monthly_report: val })}
              label="Monatlicher Bericht"
              description="Zusammenfassung Ihrer Aktivitäten"
            />
            <ToggleSwitch
              checked={settings.email_marketing}
              onChange={(val) => setSettings({ ...settings, email_marketing: val })}
              label="Marketing & Neuigkeiten"
              description="Informationen zu neuen Features und Angeboten"
            />
          </div>
        </div>

        {/* In-App Benachrichtigungen */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-xl">
              <Bell className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">In-App Benachrichtigungen</h2>
              <p className="text-sm text-gray-500">Benachrichtigungen im Dashboard</p>
            </div>
          </div>

          <div className="space-y-1">
            <ToggleSwitch
              checked={settings.push_new_case_assigned}
              onChange={(val) => setSettings({ ...settings, push_new_case_assigned: val })}
              label="Neue Fallzuweisung"
              description="Sofortige Benachrichtigung bei neuer Zuweisung"
            />
            <ToggleSwitch
              checked={settings.push_new_case_status}
              onChange={(val) => setSettings({ ...settings, push_new_case_status: val })}
              label="Status-Updates"
              description="Echtzeit-Updates im Dashboard"
            />
            <ToggleSwitch
              checked={settings.push_case_completed}
              onChange={(val) => setSettings({ ...settings, push_case_completed: val })}
              label="Gutachten fertig"
              description="Sofortige Benachrichtigung bei Fertigstellung"
            />
            <ToggleSwitch
              checked={settings.push_new_message}
              onChange={(val) => setSettings({ ...settings, push_new_message: val })}
              label="Neue Nachrichten"
              description="Chat-Benachrichtigungen in Echtzeit"
            />
            <ToggleSwitch
              checked={settings.push_payment_received}
              onChange={(val) => setSettings({ ...settings, push_payment_received: val })}
              label="Zahlungseingänge"
              description="Benachrichtigung bei Zahlungen"
            />
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Hinweis zu E-Mail-Benachrichtigungen</p>
              <p>Wichtige System-E-Mails wie Passwort-Zurücksetzung oder Sicherheitswarnungen können nicht deaktiviert werden.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PartnerNotificationSettingsPage;
